import { forwardRef, useCallback, useEffect, useId, useRef } from "react";
import type { TextareaHTMLAttributes } from "react";
import "./Input.css";
import "./Textarea.css";

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  hint?: string;
  error?: string;
  /** Cap on how many rows the field will grow to before it scrolls internally. */
  maxRows?: number;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, hint, error, maxRows = 6, id, className, onInput, rows, ...rest },
  ref
) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const hintId = `${fieldId}-hint`;
  const errorId = `${fieldId}-error`;
  const describedBy = [hint && hintId, error && errorId].filter(Boolean).join(" ") || undefined;

  const innerRef = useRef<HTMLTextAreaElement | null>(null);

  const setRefs = useCallback(
    (node: HTMLTextAreaElement | null) => {
      innerRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;
    },
    [ref]
  );

  const resize = useCallback(() => {
    const el = innerRef.current;
    if (!el) return;
    // Auto-grow by measuring scrollHeight against a line-height-derived cap;
    // resetting height first is required or scrollHeight only ever grows.
    el.style.height = "auto";
    const lineHeight = parseFloat(getComputedStyle(el).lineHeight || "20");
    const maxHeight = lineHeight * maxRows;
    const next = Math.min(el.scrollHeight, maxHeight);
    el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight > maxHeight ? "auto" : "hidden";
  }, [maxRows]);

  useEffect(() => {
    resize();
  }, [resize, rest.value]);

  return (
    <div className={["ui-field", className ?? ""].filter(Boolean).join(" ")}>
      {label && (
        <label className="ui-field__label" htmlFor={fieldId}>
          {label}
        </label>
      )}
      <div className={["ui-field__control", "ui-field__control--textarea", error ? "ui-field__control--error" : ""].filter(Boolean).join(" ")}>
        <textarea
          ref={setRefs}
          id={fieldId}
          className="ui-field__input ui-textarea"
          rows={rows ?? 1}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          onInput={(e) => {
            resize();
            onInput?.(e);
          }}
          {...rest}
        />
      </div>
      {error ? (
        <p className="ui-field__error" id={errorId}>
          {error}
        </p>
      ) : (
        hint && (
          <p className="ui-field__hint" id={hintId}>
            {hint}
          </p>
        )
      )}
    </div>
  );
});
